import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { z } from "zod";

export const Route = createFileRoute("/pers")({
  head: () => ({
    meta: [
      { title: "Pers & accreditatie — Openluchtfilmfestival Apeldoorn" },
      { name: "description", content: "Vraag een persaccreditatie aan voor het Openluchtfilmfestival in Stadspark Berg & Bos, 14 — 22 augustus 2026." },
      { property: "og:title", content: "Pers & accreditatie — Openluchtfilmfestival Apeldoorn" },
      { property: "og:description", content: "Voor journalisten, fotografen en media: vraag hier je accreditatie of persinformatie aan." },
    ],
  }),
  component: PressPage,
});

const REQUEST_TYPES = [
  { id: "accreditation", label: "Accreditatie (toegang festival)" },
  { id: "interview", label: "Interview­verzoek" },
  { id: "press_kit", label: "Perskit / persinformatie" },
  { id: "photo_video", label: "Foto- of video-opnamen" },
  { id: "other", label: "Anders" },
];

const FESTIVAL_DATES: { iso: string; label: string }[] = [
  { iso: "2026-08-14", label: "vr 14 aug" },
  { iso: "2026-08-15", label: "za 15 aug" },
  { iso: "2026-08-16", label: "zo 16 aug" },
  { iso: "2026-08-17", label: "ma 17 aug" },
  { iso: "2026-08-18", label: "di 18 aug" },
  { iso: "2026-08-19", label: "wo 19 aug" },
  { iso: "2026-08-20", label: "do 20 aug" },
  { iso: "2026-08-21", label: "vr 21 aug" },
  { iso: "2026-08-22", label: "za 22 aug" },
];

const schema = z.object({
  name: z.string().trim().min(1, "Vul je naam in").max(100),
  email: z.string().trim().email("Vul een geldig e-mailadres in").max(320),
  phone: z.string().trim().max(40).optional(),
  outlet: z.string().trim().min(1, "Vul je medium of organisatie in").max(150),
  role: z.string().trim().max(100).optional(),
  website: z.string().trim().max(300).optional(),
  request_type: z.string().min(1, "Kies een type verzoek"),
  selected_dates: z.array(z.string()).max(9),
  message: z.string().trim().max(1500).optional(),
});

function PressPage() {
  const [submitting, setSubmitting] = useState(false);
  const [requestType, setRequestType] = useState<string>("");
  const [dates, setDates] = useState<string[]>([]);

  function toggleDate(iso: string, checked: boolean) {
    setDates((prev) => (checked ? [...prev, iso] : prev.filter((d) => d !== iso)));
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? "").trim() || undefined,
      outlet: String(fd.get("outlet") ?? ""),
      role: String(fd.get("role") ?? "").trim() || undefined,
      website: String(fd.get("website") ?? "").trim() || undefined,
      request_type: requestType,
      selected_dates: dates,
      message: String(fd.get("message") ?? "").trim() || undefined,
    };
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Controleer het formulier");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("press_requests").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone ?? null,
      outlet: parsed.data.outlet,
      role: parsed.data.role ?? null,
      website: parsed.data.website ?? null,
      request_type: parsed.data.request_type,
      selected_dates: parsed.data.selected_dates,
      message: parsed.data.message ?? null,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Er ging iets mis. Probeer het opnieuw.");
      return;
    }
    toast.success("Bedankt! We nemen zo snel mogelijk contact met je op.");
    (e.target as HTMLFormElement).reset();
    setRequestType("");
    setDates([]);
  }

  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background">
        <Nav />
        <main className="pt-28 pb-24 md:pt-36">
          <section className="mx-auto max-w-3xl px-5 md:px-8">
            <div className="mb-10">
              <p className="font-mono-cinema text-[10px] text-gold">— Pers</p>
              <h1 className="mt-3 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Pers &amp; <span className="italic text-gold">accreditatie</span>
              </h1>
              <p className="mt-5 max-w-xl text-foreground/75">
                Ben je journalist, fotograaf, videomaker of werk je voor een medium? Vraag hier je
                accreditatie of persinformatie aan voor het Openluchtfilmfestival Apeldoorn,
                14 — 22 augustus 2026.
              </p>
            </div>

            <form
              onSubmit={onSubmit}
              className="film-grain space-y-7 rounded-3xl border border-border bg-gradient-to-br from-night to-night-deep p-6 shadow-soft md:p-10"
            >
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-widest text-foreground/75">Naam</Label>
                  <Input id="name" name="name" required className="h-12 border-border bg-night-deep/60" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest text-foreground/75">E-mailadres</Label>
                  <Input id="email" name="email" type="email" required className="h-12 border-border bg-night-deep/60" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-foreground/75">
                    Telefoon <span className="normal-case text-foreground/50">(optioneel)</span>
                  </Label>
                  <Input id="phone" name="phone" type="tel" className="h-12 border-border bg-night-deep/60" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="outlet" className="text-xs uppercase tracking-widest text-foreground/75">Medium / organisatie</Label>
                  <Input id="outlet" name="outlet" required className="h-12 border-border bg-night-deep/60" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-xs uppercase tracking-widest text-foreground/75">
                    Functie <span className="normal-case text-foreground/50">(optioneel)</span>
                  </Label>
                  <Input id="role" name="role" placeholder="bv. journalist, fotograaf" className="h-12 border-border bg-night-deep/60" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website" className="text-xs uppercase tracking-widest text-foreground/75">
                    Website / publicatie <span className="normal-case text-foreground/50">(optioneel)</span>
                  </Label>
                  <Input id="website" name="website" placeholder="https://…" className="h-12 border-border bg-night-deep/60" />
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs uppercase tracking-widest text-foreground/75">Type verzoek</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {REQUEST_TYPES.map((r) => (
                    <label
                      key={r.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${
                        requestType === r.id
                          ? "border-gold/70 bg-night-deep/70"
                          : "border-border bg-night-deep/40 hover:border-gold/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="request_type"
                        value={r.id}
                        checked={requestType === r.id}
                        onChange={() => setRequestType(r.id)}
                        className="h-4 w-4 accent-[hsl(var(--gold))]"
                      />
                      <span className="text-sm">{r.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs uppercase tracking-widest text-foreground/75">
                  Gewenste avond(en) <span className="normal-case text-foreground/50">(optioneel)</span>
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                  {FESTIVAL_DATES.map((d) => (
                    <label
                      key={d.iso}
                      className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-night-deep/40 p-4 hover:border-gold/50"
                    >
                      <Checkbox
                        checked={dates.includes(d.iso)}
                        onCheckedChange={(c) => toggleDate(d.iso, Boolean(c))}
                      />
                      <span className="text-sm">{d.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-xs uppercase tracking-widest text-foreground/75">
                  Bericht <span className="normal-case text-foreground/50">(optioneel)</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={1500}
                  placeholder="Vertel kort waar je aan werkt, welk medium je vertegenwoordigt en waar we je mee kunnen helpen."
                  className="border-border bg-night-deep/60"
                />
              </div>

              <Button type="submit" variant="hero" size="xl" disabled={submitting} className="w-full sm:w-auto">
                {submitting ? "Een moment…" : "Verstuur persaanvraag"}
              </Button>

              <p className="text-xs text-foreground/60">
                Vragen? Mail direct naar{" "}
                <a href="mailto:pers@openluchtfilmfestival.nl" className="text-gold hover:underline">
                  pers@openluchtfilmfestival.nl
                </a>
                .
              </p>
            </form>
          </section>
        </main>
        <Footer />
        <Toaster position="top-center" />
      </div>
    </I18nProvider>
  );
}